import './Dashboard.css';
import 'containers/Dazzle/lib/style/style.css';

import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import { PureComponent } from 'react';
import {
  addRow,
  addWidget,
  clearDashboard,
  createReportGroup,
  deleteLayout,
  deleteReportGroup,
  fetchReports,
  moveColumn,
  moveWidget,
  removeRow,
  removeWidget,
  renameLayout,
  saveLayout,
  sortColumns,
  sortWidgets,
  switchEditLayoutMode,
  updateColumnProperty,
  updateReportGroup,
  updateWidget,
} from './actions';

import { isNonNullable } from '@ipfabric/utils';
import Chart from 'chart.js';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import Dazzle from 'containers/Dazzle/lib';
import LeaveDialogue from 'containers/LeaveDialogue';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { Permissions } from 'containers/Permissions/Permissions';
import EditBar from 'pages/Dashboard/Overview/EditBar';
import { makeSelectedSnapshot } from 'pages/SnapshotManagement/selectors';
import PropTypes from 'prop-types';
import { assoc } from 'ramda';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import handleEvent from 'utils/handleEvent';
import AddRowDialog from './AddRowDialog';
import AddWidget from './AddWidget';
import CustomFrame from './CustomFrame';
import OverviewPropTypes from './OverviewPropTypes';
import { makeSelectDashboard } from './selectors';
import DeviceChart from './widgets/DeviceChart';
import HostVendorMix from './widgets/HostVendorMix';
import NetworkVendorMix from './widgets/NetworkVendorMix';
import TableColor from './widgets/TableColor';
import TextRow from './widgets/TextRow';

class Dashboard extends PureComponent {
  static propTypes = {
    allMessages: PropTypes.object.isRequired,
    dashboard: OverviewPropTypes.dashboard,
    navigate: PropTypes.func.isRequired,
    msg: PropTypes.object.isRequired,
    onAddRow: PropTypes.func.isRequired,
    onAddWidget: PropTypes.func.isRequired,
    onClearDashboard: PropTypes.func.isRequired,
    onCreateReportGroup: PropTypes.func.isRequired,
    onDeleteReportGroup: PropTypes.func.isRequired,
    onFetchReports: PropTypes.func.isRequired,
    onMoveColumn: PropTypes.func.isRequired,
    onMoveWidget: PropTypes.func.isRequired,
    onRemoveRow: PropTypes.func.isRequired,
    onRemoveWidget: PropTypes.func.isRequired,
    onSaveLayout: PropTypes.func.isRequired,
    onSortColumns: PropTypes.func.isRequired,
    onSortWidgets: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    onUpdateColumnProperty: PropTypes.func.isRequired,
    onUpdateReportGroup: PropTypes.func.isRequired,
    onUpdateWidget: PropTypes.func.isRequired,
    selectedSnapshot: OverviewPropTypes.selectedSnapshot,
    dateFormat: PropTypes.string.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    user: PropTypes.object.isRequired,
    onDeleteLayout: PropTypes.func.isRequired,
    onSwitchEditLayoutMode: PropTypes.func.isRequired,
    onRenameLayout: PropTypes.func.isRequired,
  };

  state = {
    isAddWidgetOpen: false,
    isAddRowOpen: false,
    addWidgetOptions: {},
    addWidgetStep: 1,
  };

  handleRemoveWidget = handleEvent(({ rowIndex, columnIndex, widgetIndex }) => {
    const {
      dashboard: { layout, reportsGroups },
      onDeleteReportGroup,
      onRemoveWidget,
    } = this.props;
    const foundWidget = get(layout, [
      'rows',
      rowIndex,
      'columns',
      columnIndex,
      'widgets',
      widgetIndex,
    ]);
    onRemoveWidget({ rowIndex, columnIndex, widgetIndex });
    const groupId = get(foundWidget, 'props.group');
    if (
      isNonNullable(groupId) &&
      reportsGroups.findIndex((g) => g.id === groupId) > -1
    ) {
      onDeleteReportGroup({ groupId });
    }
  });

  handleRemoveRow = handleEvent((rowIndex) => {
    const { onRemoveRow } = this.props;
    onRemoveRow({ rowIndex });
  });

  handleAdd = handleEvent((rowIndex, columnIndex) => {
    this.setState({
      isAddWidgetOpen: true,
      addWidgetStep: 1,
      addWidgetOptions: {
        rowIndex,
        columnIndex,
      },
    });
  });

  handleClose = handleEvent((cb) => {
    this.setState(
      {
        isAddWidgetOpen: false,
        editWidget: false,
        addWidgetStep: 1,
      },
      () => isFunction(cb) && cb(),
    );
  });

  handleCloseRow = handleEvent(() => {
    this.setState({
      isAddRowOpen: false,
      addWidgetStep: 1,
    });
  });

  handleWidgetSelection = handleEvent(
    (
      widgetName,
      values = {},
      _type = undefined,
      disableLayoutUpdate = false,
    ) => {
      const { onUpdateWidget, onAddWidget } = this.props;
      const {
        editWidget,
        addWidgetOptions: { rowIndex, columnIndex, widgetIndex },
      } = this.state;
      this.handleClose(() => {
        if (!disableLayoutUpdate) {
          if (editWidget) {
            onUpdateWidget({
              rowIndex,
              columnIndex,
              widgetName,
              widgetIndex,
              props: values,
            });
          } else {
            onAddWidget({
              rowIndex,
              columnIndex,
              widgetName,
              props: values,
            });
          }
        }
      });
    },
  );

  handleAddRow = handleEvent((row) => {
    const { onAddRow } = this.props;
    const { rowIndex } = this.state.addWidgetOptions;
    onAddRow({ rowIndex, row });
    this.handleCloseRow();
  });

  handleAddRowClick = handleEvent((rowIndex) => {
    this.setState({
      isAddRowOpen: true,
      addWidgetOptions: {
        rowIndex,
        columnIndex: null,
      },
    });
  });

  handleEditWidgetClick = handleEvent(
    ({ rowIndex, columnIndex, widgetIndex }) => {
      const {
        dashboard: { layout },
      } = this.props;
      const widget = get(layout, [
        'rows',
        rowIndex,
        'columns',
        columnIndex,
        'widgets',
        widgetIndex,
      ]);
      this.setState({
        isAddWidgetOpen: true,
        editWidget: true,
        addWidgetStep: 2,
        addWidgetOptions: {
          rowIndex,
          columnIndex,
          widgetIndex,
          widget: widget.key,
          widgetProps: widget.props,
        },
      });
    },
  );

  handleUpdateColumnProperty = handleEvent(
    (rowIndex, columnIndex, property, value) => {
      const { onUpdateColumnProperty } = this.props;
      onUpdateColumnProperty({
        rowIndex,
        columnIndex,
        property,
        value,
      });
    },
  );

  handleLeaveDialogue = handleEvent(() => {
    const { onSwitchEditLayoutMode } = this.props;

    // turn edit-layout-mode off before you leave
    onSwitchEditLayoutMode({ editMode: false });
  });

  handleLeaveDialogueSave = handleEvent((returnUrl) => {
    const {
      dashboard: { layout, layoutsDict },
      onSaveLayout,
      navigate,
    } = this.props;
    onSaveLayout({ layout, layoutsDict });
    const { state = {} } = returnUrl;
    navigate(
      {
        pathname: returnUrl.pathname,
        search: returnUrl.search,
        hash: returnUrl.hash,
      },
      { state: { ...state, canLeave: true } },
    );
  });

  handleStepChange = handleEvent((step) => {
    this.setState({ addWidgetStep: step });
  });

  render() {
    const {
      allMessages,
      dashboard: {
        layout,
        reports,
        reportsGroups,
        selectedGroup,
        discoveryHistory,
        networkVendorMix,
        hostVendorMix,
        textRow,
        dashboardLoading,
        dataModified,
        isEditLayoutMode,
      },
      navigate,
      msg: { dashboard: msg },
      dateFormat,
      dateTimeFormat,
      onCreateReportGroup,
      onFetchReports,
      onMoveColumn,
      onMoveWidget,
      onSortColumns,
      onSortWidgets,
      onToggleConfirmDialog,
      onUpdateReportGroup,
      user,
    } = this.props;
    const {
      addWidgetOptions: {
        rowIndex,
        columnIndex,
        widgetIndex,
        widget,
        widgetProps,
      },
      addWidgetStep,
      editWidget,
      isAddRowOpen,
      isAddWidgetOpen,
    } = this.state;
    const reportsMap = reports?.reduce((acc, report) => {
      return assoc(report.id, report, acc);
    }, {});
    const widgets = {
      TextRow: {
        type: TextRow,
        title: get(msg, ['widgets', 'textRow']),
        showTitle: false,
        props: { msg, data: { textRow } },
      },
      NetworkVendorMix: {
        disableEdit: true,
        type: NetworkVendorMix,
        title: get(msg, ['widgets', 'networkVendorMix']),
        showTitle: false,
        props: { msg, Chart, data: { networkVendorMix } },
      },
      HostVendorMix: {
        disableEdit: true,
        type: HostVendorMix,
        title: get(msg, ['widgets', 'hostVendorMix']),
        showTitle: false,
        props: { msg, Chart, data: { hostVendorMix } },
      },
      TableColor: {
        type: TableColor,
        title: get(msg, ['widgets', 'tableColor']),
        showTitle: false,
        props: {
          msg,
          Chart,
          data: { reportsGroups, reportsMap },
        },
      },
      DeviceChart: {
        disableEdit: true,
        type: DeviceChart,
        title: get(msg, ['widgets', 'deviceChart', 'title']),
        showTitle: false,
        props: {
          msg,
          Chart,
          navigate,
          data: { discoveryHistory },
          dateFormat,
          dateTimeFormat,
          timezone: user.timezone,
        },
      },
    };

    if (dashboardLoading) {
      return (
        <div className='box box-solid mb-0 flex flex-1 items-center'>
          <LoaderAndText />
        </div>
      );
    }

    return (
      <div className='dashboard m-4'>
        <Helmet title='Dashboard' />
        {isAddRowOpen ? (
          <AddRowDialog
            msg={msg}
            onClose={this.handleCloseRow}
            onSubmit={this.handleAddRow}
          />
        ) : null}
        <LeaveDialogue
          history={history}
          isPristine={!isEditLayoutMode && !dataModified}
          onSave={this.handleLeaveDialogueSave}
          onLeave={this.handleLeaveDialogue}
          msg={get(msg, 'leaveDialogue')}
          isValid
        />
        {isAddWidgetOpen ? (
          <AddWidget
            allMessages={allMessages}
            Chart={Chart}
            columnIndex={columnIndex}
            editWidget={editWidget}
            layout={layout}
            msg={msg}
            onAddRow={this.handleAddRow}
            onCreateReportGroup={onCreateReportGroup}
            onFetchReports={onFetchReports}
            onRequestClose={this.handleClose}
            onStepChange={this.handleStepChange}
            onToggleConfirmDialog={onToggleConfirmDialog}
            onUpdateReportGroup={onUpdateReportGroup}
            onWidgetSelect={this.handleWidgetSelection}
            reports={reports}
            reportsGroups={reportsGroups}
            rowIndex={rowIndex}
            selectedGroup={selectedGroup}
            step={addWidgetStep}
            textRow={textRow}
            widget={widget}
            widgetIndex={widgetIndex}
            widgetProps={widgetProps}
            widgets={widgets}
          />
        ) : null}
        {!isAddWidgetOpen || (isAddWidgetOpen && addWidgetStep <= 1) ? (
          <div>
            <div className='mb-4'>
              <Permissions requiredScope={['settings']}>
                <EditBar {...this.props} />
              </Permissions>
            </div>
            <div>
              {reportsGroups && reports && (
                <Dazzle
                  editable={isEditLayoutMode}
                  frameComponent={CustomFrame}
                  layout={layout}
                  msg={msg}
                  onAdd={this.handleAdd}
                  onAddRow={this.handleAddRowClick}
                  onEdit={this.handleEditWidgetClick}
                  onMoveColumn={onMoveColumn}
                  onMoveWidget={onMoveWidget}
                  onRemove={this.handleRemoveWidget}
                  onRemoveRow={this.handleRemoveRow}
                  onSortColumns={onSortColumns}
                  onSortWidgets={onSortWidgets}
                  onToggleConfirmDialog={onToggleConfirmDialog}
                  onUpdateColumnProperty={this.handleUpdateColumnProperty}
                  titlePlaceholder={get(msg, 'titlePlaceholder')}
                  widgets={widgets}
                />
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  dashboard: makeSelectDashboard(),
  selectedSnapshot: makeSelectedSnapshot(),
});

function mapDispatchToProps(dispatch) {
  return {
    onAddRow: (args) => dispatch(addRow(args)),
    onAddWidget: (args) => dispatch(addWidget(args)),
    onClearDashboard: (args) => dispatch(clearDashboard(args)),
    onCreateReportGroup: (args) => dispatch(createReportGroup(args)),
    onDeleteReportGroup: (args) => dispatch(deleteReportGroup(args)),
    onFetchReports: (args) => dispatch(fetchReports(args)),
    onMoveColumn: (args) => dispatch(moveColumn(args)),
    onMoveWidget: (args) => dispatch(moveWidget(args)),
    onRemoveRow: (args) => dispatch(removeRow(args)),
    onRemoveWidget: (args) => dispatch(removeWidget(args)),
    onSaveLayout: (args) => dispatch(saveLayout(args)),
    onSortColumns: (args) => dispatch(sortColumns(args)),
    onSortWidgets: (args) => dispatch(sortWidgets(args)),
    onToggleConfirmDialog: (args) => dispatch(toggleConfirmDialog(args)),
    onUpdateColumnProperty: (args) => dispatch(updateColumnProperty(args)),
    onUpdateReportGroup: (args) => dispatch(updateReportGroup(args)),
    onUpdateWidget: (args) => dispatch(updateWidget(args)),
    onDeleteLayout: (args) => dispatch(deleteLayout(args)),
    onSwitchEditLayoutMode: (args) => dispatch(switchEditLayoutMode(args)),
    onRenameLayout: (args) => dispatch(renameLayout(args)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);
